import React, { ReactNode, useState } from 'react';
import CloseIcon from '../icons/CloseIcon';
import styled from '@emotion/styled';

const PopoverContainer = styled.div<StyleProps>`
  position: absolute;
  display: block;
  width: auto;
  transform: translate(${s => s.position.left}, ${s => s.position.top});
  visibility: ${s => (s.visible ? 'visible' : 'hidden')};
  box-shadow: rgba(0, 0, 0, 0.3) 0 0.4rem 1rem 0rem;
  border-radius: 0.3125rem;
  z-index: 25;
  top: 0px;
  left: 0px;
`;

const TextContainer = styled.div`
  position: relative;
  background: #fff;
  bottom: 0;
  padding: 1rem;
  max-width: 300px;
  color: #566072;
  font-size: 0.8rem;
  line-height: 1.2rem;
  border-radius: 0.3125rem;
  width: auto;
  z-index: 25;

  &:after {
    position: absolute;
    content: '\\25BC';
    display: block;
    color: #fff;
    margin-top: 1.1rem;
    z-index: 25;
    left: 50%;
    margin-right: -50%;
    transform: translate(-50%, -50%);
  }
`;

const Text = styled.div`
  margin: 0;
  padding: 0;
`;

const ToggleTarget = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  max-width: 22px;
`;

const HideTarget = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  cursor: pointer;
  position: absolute;
  top: 0.4rem;
  right: 0.4rem;

  svg {
    height: 0.6rem;
    width: 0.6rem;
    fill: #bdcad2;
  }
`;

const RelativeWrapper = styled.div`
  position: relative;
  flex: 1;
`;

interface Position {
  top: string;
  left: string;
}

interface StyleProps {
  position: Position;
  visible: boolean;
}

interface Props {
  text?: string;
  rawHtml?: string;
  content?: ReactNode;
  children: ReactNode;
}

export const Popover: React.FC<Props> = (props: Props) => {
  const offScreen = '-99999px';
  const defaultPosition = { top: offScreen, left: offScreen };
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<Position>(defaultPosition);

  let toggleTarget: HTMLElement | null = null;
  let popoverContainer: HTMLElement | null = null;

  const toggle = () => {
    if (toggleTarget && popoverContainer) {
      const targetMidpoint = toggleTarget.clientWidth / 2;
      const leftMostPopoverPosition = toggleTarget.offsetLeft + targetMidpoint;
      const popoverOffset = popoverContainer.clientWidth / 2;
      const popoverMidpoint = leftMostPopoverPosition - popoverOffset;

      const pseudoElementPadding = 10;
      const height = popoverContainer.clientHeight + pseudoElementPadding;

      const top = `${-height}px`;
      const left = `${popoverMidpoint}px`;

      setVisible(prev => !prev);
      setPosition(prev => {
        return {
          top: prev.top === top ? offScreen : top,
          left: prev.left === left ? offScreen : left,
        };
      });
    } else {
      throw new Error('Popover elements not found');
    }
  };

  const hide = () => {
    setVisible(prev => !prev);
    setPosition(defaultPosition);
  };

  function createMarkup(html: string) {
    return { __html: html };
  }
  const haveContent = props.text !== undefined || props.content !== undefined;
  const content = haveContent ? props.text || props.content : undefined;

  return (
    <RelativeWrapper>
      <ToggleTarget
        ref={el => (toggleTarget = el)}
        onClick={toggle}
        data-testid="popover-click-target"
      >
        {props.children}
      </ToggleTarget>
      <PopoverContainer
        ref={el => (popoverContainer = el)}
        position={position}
        visible={visible}
        data-testid="popover-text-container"
      >
        <TextContainer>
          <HideTarget onClick={hide}>
            <CloseIcon />
          </HideTarget>
          {haveContent && <Text data-testid="popover-text">{content}</Text>}
          {props.rawHtml && (
            <Text
              data-testid="popover-raw-html"
              dangerouslySetInnerHTML={createMarkup(props.rawHtml)}
            />
          )}
        </TextContainer>
      </PopoverContainer>
    </RelativeWrapper>
  );
};

export default Popover;
